import CheckOutlineIcon from "@rsuite/icons/CheckOutline";
import ReloadIcon from "@rsuite/icons/Reload";
import type React from "react";
import { useState } from "react";
import { ButtonToolbar, IconButton } from "rsuite";
import type { SentencePart } from "../../../entities/Activity/types/InteractiveSentences";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { CustomAudioPlayer } from "../../../shared/components/AudioPlayer/AudioPlayer";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import styles from "./InteractiveSentence.module.scss";

type Props = {
	sentences: SentencePart[][];
	audio: string;
	activityId: string;
};

export const InteractiveSentence: React.FC<Props> = ({
	sentences,
	audio = "",
	activityId = "",
}) => {
	const [selectedOptions, setSelectedOptions] = useState<
		Record<string, string>
	>({});
	const [isChecked, setIsChecked] = useState(false);
	const [score, setScore] = useState<number | null>(null);
	const { triggerActivityCompletion } = useActivityCompletion();
	const [fireworksTrigger, setFireworksTrigger] = useState(false);

	const handleSelectOption = (partId: string, optionId: string) => {
		setSelectedOptions((prev) => ({ ...prev, [partId]: optionId }));
	};

	const handleCheckAnswers = async () => {
		let correctAnswers = 0;
		let totalQuestions = 0;

		for (const sentence of sentences) {
			for (const part of sentence) {
				if (Array.isArray(part)) {
					totalQuestions++;
					const selectedOption = part.find(
						(option) => option.id === selectedOptions[part[0].id],
					);
					if (selectedOption?.status) {
						correctAnswers++;
					}
				}
			}
		}

		setScore(correctAnswers);
		setIsChecked(true);

		await triggerActivityCompletion(activityId, correctAnswers, resetActivity);

		setFireworksTrigger(true);
	};

	const allAnswered = sentences?.every((sentence) =>
		sentence?.every(
			(part) =>
				typeof part === "string" ||
				(Array.isArray(part) && selectedOptions[part[0].id]),
		),
	);

	const resetActivity = () => {
		setSelectedOptions({});
		setIsChecked(false);
		setScore(null);
	};

	const handleFireworksComplete = () => {
		setFireworksTrigger(false);
	};

	return (
		<div className={styles.container}>
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={handleFireworksComplete}
				/>
			)}
			<div className={styles.toolbar}>
				<ButtonToolbar>
					<IconButton
						onClick={resetActivity}
						appearance="primary"
						icon={<ReloadIcon color="white" />}
					/>
					<IconButton
						disabled={!allAnswered}
						onClick={handleCheckAnswers}
						appearance="primary"
						icon={<CheckOutlineIcon color="white" />}
					>
						Check
					</IconButton>
				</ButtonToolbar>
				<div className={styles.audio}>
					{audio && <CustomAudioPlayer src={audio} />}
				</div>
			</div>
			<div className={styles.innerContainer}>
				{sentences?.map((sentence, sentenceIndex) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: <index is not a problem here>
					<div key={sentenceIndex} className={styles.sentence}>
						{sentence.map((part, partIndex) =>
							typeof part === "string" ? (
								// biome-ignore lint/suspicious/noArrayIndexKey: <index is not a problem here>
								<span key={partIndex} className={styles.textPart}>
									{part}
								</span>
							) : (
								// biome-ignore lint/suspicious/noArrayIndexKey: <index is not a problem here>
								<div key={partIndex} className={styles.options}>
									{part.map((option) => (
										<button
											type="button"
											key={option.id}
											className={`${styles.option} ${
												selectedOptions[part[0].id] === option.id
													? styles.selected
													: ""
											}`}
											onClick={() => handleSelectOption(part[0].id, option.id)}
										>
											{option.word}
										</button>
									))}
								</div>
							),
						)}
					</div>
				))}
			</div>
		</div>
	);
};
